import { FormikValues, useFormikContext } from 'formik'
import React from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import * as Api from 'src/api'
import { getStudentDegreeOptions } from 'src/helpers/fns'
import { useAdminAccess, useApi } from 'src/helpers/hooks'
import { useAuthenticatedHeaders } from 'src/hooks/auth/app'
import { useLocale } from 'src/hooks/locale/locale'
import { useTranslatable } from 'src/hooks/locale/utils'
import { Button } from 'src/tailwind/components/Button'
import { Form } from 'src/views/components/forms/formik/Form'
import SelectInput from 'src/views/components/forms/formik/SelectInput'
import TextareaInput from 'src/views/components/forms/formik/TextareaInput'
import TextInput from 'src/views/components/forms/formik/TextInput'
import { useSection, useSectionData } from './SectionContext'
import {
  buildBodyWithSection,
  getSectionInitialValues,
  submitSection,
  type SectionFormValues,
} from './sectionFormState'
import { TabFormErrors } from './sectionTabForm'

export default function AdministratorPortfolioSectionGeneral(): JSX.Element {
  const section = useSectionData()
  const { mutate } = useSection()
  const headers = useAuthenticatedHeaders()
  const navigate = useNavigate()
  const locale = useLocale()
  const t = useTranslatable()
  const { id } = useParams<{ id: string }>()

  const initialValues = React.useMemo(() => getSectionInitialValues(section), [section])

  const handleSubmit = React.useCallback(
    async (values: { section: SectionFormValues }) => {
      const body = buildBodyWithSection(section, values.section)
      await submitSection({
        headers,
        id,
        body,
        navigate,
        locale,
        mutate,
        t,
      })
    },
    [section, headers, id, navigate, locale, mutate, t]
  )

  const { data: definitions } = useApi({
    endpoint: Api.getPortfolioDefinitions,
    params: React.useMemo(() => ({ headers }), [headers]),
  })
  const statusOptions = React.useMemo(() => definitions?.statuses ?? [], [definitions])
  const workTypeOptions = React.useMemo(() => definitions?.workTypes ?? [], [definitions])
  const degreeOptions = getStudentDegreeOptions()

  return (
    <Form initialValues={initialValues} enableReinitialize onSubmit={handleSubmit} classNames={{ form: 'w-full' }}>
      <div className="mt-6 w-full rounded-card bg-card px-6 py-9">
        <TabFormErrors />
        <div className="mb-2">
          <TextareaInput name="section.name" label={t('common:name')} rows={5} required />
        </div>
        <div className="mb-2">
          <TextareaInput name="section.nameEn" label={t('common:name_en')} rows={5} required />
        </div>
        <div className="mb-2">
          <TextInput name="section.min" label={t('common:min')} type="number" required />
        </div>
        <div className="mb-2">
          <TextInput name="section.max" label={t('common:max')} type="number" required />
        </div>
        <div className="mb-2">
          <SelectInput name="section.degree" label={t('person:degree')} options={degreeOptions} required />
        </div>
        <div className="mb-2">
          <SelectInput name="section.status" label={t('common:status')} options={statusOptions} required />
        </div>
        <div className="mb-2">
          <SelectInput name="section.workType" label={t('portfolio:work_type')} options={workTypeOptions} required />
        </div>
        <div className="mb-2">
          <TextareaInput name="section.workTitle" label={t('portfolio:assignment_name')} rows={5} />
        </div>
        <div className="mb-2">
          <TextareaInput name="section.workTitleEn" label={t('portfolio:assignment_name_en')} rows={5} />
        </div>
        <SaveButton />
      </div>
    </Form>
  )
}

function SaveButton(): React.ReactElement | null {
  const t = useTranslatable()
  const formik = useFormikContext<FormikValues>()
  const canManagePortfolio = useAdminAccess().canManage('portfolio')
  if (!canManagePortfolio) return null
  return (
    <div className="my-4 flex flex-wrap justify-end">
      <Button
        type="submit"
        className="ml-2"
        variant="red"
        disabled={formik.isSubmitting || !formik.dirty || !formik.isValid}
      >
        {t('common:save')}
      </Button>
    </div>
  )
}
